import React, { useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Formik, Form } from 'formik';
import Steps from 'components/Steps'
import validationSchema from 'containers/Onboarding/FormModels/Validation';
import formModel from 'containers/Onboarding/FormModels/Models';
import initialValues from 'containers/Onboarding/FormModels/InitialValue'
import useStyles from 'containers/Onboarding/styles';
import dataStorage from 'dataStorage';
import {
    clone,
    scrollToTop,
    handleDataBeforeSubmit,
    getKycUrlData
} from 'helpers/utils';
import FocusError from 'components/FocusError'
import VerifyPhoneNumber from 'components/VerifyPhoneNumber'
import RefferredCode from 'components/RefferredCode'
import logger from 'helpers/logger'
import UploadIdPhoto from 'containers/Onboarding/Screens/UploadIdPhoto'
import EKYC from 'containers/Onboarding/Screens/EKYC'
import PersonalInformation from 'containers/Onboarding/Screens/PersonalInformation'
import EmploymentInformation from 'containers/Onboarding/Screens/EmploymentInformation'
import FinancialAndInvestment from 'containers/Onboarding/Screens/FinancialAndInvestment'
import MakeADeposit from 'containers/Onboarding/Screens/MakeADeposit'
import Esignature from 'containers/Onboarding/Screens/Esignature'
import AccountType from 'containers/Onboarding/Screens/AccountType'
import RiskDisclosureAndDeclarationSignature from 'containers/Onboarding/Screens/RiskDisclosureAndDeclarationSignature'
import OtherIdentityDisclosuresAndTaxInformation from 'containers/Onboarding/Screens/OtherIdentityDisclosuresAndTaxInformation'
import ListStep from 'containers/Onboarding/FormModels/ListStep'
import { useSaveDraft } from 'hooks/useDraft'
import {
    useTransaction
} from 'hooks/useKYC'
import {
    useSubmitAccount
} from 'hooks/useAccount'
import { Trans } from 'react-i18next'
import { scopedCssBaselineClasses } from '@mui/material';
import moment from 'moment'

const { formId, formField } = formModel;

export default function Onboarding(props) {
    const { data: draft } = props
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(props.step || 0);
    const listStepCanPress = React.useRef({ 0: false, 1: false, 2: props.step > 1 })
    const initialData = React.useRef({
        ...clone(initialValues),
        ...draft
    });
    const submitStep = ListStep.length - 2;
    const lastStep = ListStep.length - 1;
    const currentValidationSchema = validationSchema[activeStep];
    const refNext = React.useRef()
    const kycUrl = React.useRef(props.kycUrl || {})
    const kycResult = React.useRef()
    const formValues = React.useRef()
    const formActions = React.useRef()
    const refKyc = React.useRef()
    const livenessEnable = React.useRef(true)
    const { mutate: callSaveDraft } = useSaveDraft()
    const [allow, setAllow] = React.useState(!(props.step > 1))
    const { isLoading: isVerifying, data: transaction } = useTransaction({
        enabled: allow,
        refetchInterval: allow ? 3000 : false
    })
    const { mutate: callSubmit } = useSubmitAccount()

    React.useEffect(() => {
        if (props.step > 1) return
        let result = {}
        if (transaction?.kyc_results) {
            result = JSON.parse(transaction.kyc_results)
            result = result.reduce((acc, cur) => {
                acc[cur.category] = cur
                return acc
            }, {})
            const photoMatchFailedCount = result?.PHOTO_MATCH?.data?.failed_count || 0
            const livenessFailedCount = result?.LIVENESS?.data?.failed_count || 0
            const curPhotoMatchFailedCount = kycResult.current?.PHOTO_MATCH?.data?.failed_count || 0
            const curLivenessFailedCount = kycResult.current?.LIVENESS?.data?.failed_count || 0
            kycResult.current = result
            if (result?.PHOTO_MATCH?.decision + result?.DOCUMENT?.decision + result?.LIVENESS?.decision === 'PASSEDPASSEDPASSED') {
                const passportInfo = result?.DOCUMENT?.data
                formValues.current.personal_information.eng_given_name = passportInfo.lastName
                formValues.current.personal_information.eng_sur_name = passportInfo.firstName
                formValues.current.personal_information.dob = passportInfo.dateOfBirth
                formValues.current.personal_information.id_number = passportInfo.documentNumber
                dataStorage.livenessProgress = false
                setAllow(false)
                setTimeout(() => {
                    refNext.current && refNext.current.click()
                }, 500)
            } else if (photoMatchFailedCount >= 5 || livenessFailedCount >= 5) {
                dataStorage.livenessProgress = false
                livenessEnable.current = false
                setAllow(false)
                dataStorage.showModal({
                    title: 'lang_notify',
                    content: 'lang_ekyc_over_failed_count',
                    callback: () => window.location.reload()
                })
            } else {
                let errorLabel = ''
                if (result?.LIVENESS?.details?.label && result?.LIVENESS?.details?.label !== 'OK') errorLabel = `kyc_details_${result.LIVENESS.details.label.toLowerCase()}`
                if (result?.PHOTO_MATCH?.details?.label && result?.PHOTO_MATCH?.details?.label !== 'OK') errorLabel = `kyc_details_${result.PHOTO_MATCH.details.label.toLowerCase()}`
                if (result?.DOCUMENT?.details?.label && result?.DOCUMENT?.details?.label !== 'OK') errorLabel = `kyc_details_${result.DOCUMENT.details.label.toLowerCase()}`
                if (errorLabel && dataStorage.livenessProgress) {
                    setAllow(false)
                    dataStorage.livenessProgress = false
                    dataStorage.showModal({
                        title: 'lang_notify',
                        content: errorLabel,
                        confirmText: 'lang_try_again',
                        callback: () => {
                            setAllow(true)
                            dataStorage.livenessProgress = true
                            if (activeStep === 1) refKyc.current?.recheckLiveness?.()
                            else formActions?.current?.setSubmitting?.(false)
                        }
                    })
                }
            }
        }
        if (transaction?.workflow6_web_url) {
            if (kycUrl.current === 'waiting') {
                formActions?.current?.setSubmitting?.(false)
                setTimeout(() => {
                    kycUrl.current = getKycUrlData(transaction.workflow6_web_url)
                    refNext.current && refNext.current.click()
                }, 300)
            } else {
                kycUrl.current = getKycUrlData(transaction.workflow6_web_url)
            }
        }
    }, [transaction])

    const setCheckDocument = (value) => {
        setAllow(!!value)
    }

    function _renderStepContent() {
        switch (activeStep) {
            case 0:
                return <UploadIdPhoto setCheckDocument={setCheckDocument} />;
            case 1:
                return <EKYC ref={refKyc} {...kycUrl.current} />;
            case 2:
                return <PersonalInformation />;
            case 3:
                return <EmploymentInformation />;
            case 4:
                return <FinancialAndInvestment />;
            case 5:
                return <OtherIdentityDisclosuresAndTaxInformation />;
            case 6:
                return <AccountType />;
            case 7:
                return <RiskDisclosureAndDeclarationSignature />;
            case 8:
                return <Esignature />;
            case 9:
                return <MakeADeposit />;
            default:
                return <div>Not Found</div>;
        }
    }

    async function _submitForm(values, actions) {
        const body = handleDataBeforeSubmit(clone(values), values)
        body.time = Date.now()
        if (Array.isArray(body.personal_financial_information.source_of_fund)) {
            body.personal_financial_information.source_of_fund = body.personal_financial_information.source_of_fund.join(',')
        }
        if (Array.isArray(body.account_type)) {
            body.account_type = body.account_type.join(',')
        }
        body.personal_information.dob = moment(body.personal_information.dob).format('YYYY-MM-DD')
        body.identity_disclosure = {}
        body.risk_disclosure = {}
        body.live_detection = {}
        body.bank_verification = {
            bank_name: null,
            bank_card_number: null
        }
        callSubmit(body, {
            onSuccess: (data) => {
                scrollToTop()
                callSaveDraft(values)
                listStepCanPress.current[activeStep + 1] = true
                actions.setTouched({});
                actions.setSubmitting(false);
                setActiveStep(activeStep + 1);
                console.log('Submit success')
            },
            onError: () => {
                actions.setSubmitting(false)
                console.error('Cannot post sign to server')
                dataStorage.showAlert('lang_submit_unsuccessfully')
            }
        })
    }

    const onNext = (values, actions) => {
        const callback = () => {
            scrollToTop()
            callSaveDraft(values)
            listStepCanPress.current[activeStep + 1] = true
            actions.setTouched({});
            actions.setSubmitting(false);
            setActiveStep(activeStep + 1);
        }
        switch (activeStep) {
            case 0: {
                if (!livenessEnable.current) return
                if (kycUrl?.current?.sdkToken) callback()
                else {
                    kycUrl.current = 'waiting'
                }
                break
            }
            case 1: {
                // check eKYC
                callback()
                break
            }
            case 2: {
                // Verify mobile number
                dataStorage.showModal({
                    component: VerifyPhoneNumber,
                    props: {
                        callback: (response) => {
                            values.verify_otp_token = response.verifyOtpToken
                            callback()
                        },
                        phone: values.personal_information.mobile_number
                    },
                    cancelCallback: () => actions.setSubmitting(false)
                })
                break
            }
            case 5: {
                // check all checkbox choose No
                callback()
                break
            }
            case 7: {
                // check all checkbox checked
                // show popup reference
                dataStorage.showModal({
                    component: RefferredCode,
                    props: {
                        referrerCode: values.referral_code || '',
                        callback: (referrerCode) => {
                            values.referral_code = referrerCode || ''
                            callback()
                        },
                        requiredValue: true
                    }
                })
                break
            }
            default: callback()
                break
        }
    }

    const onFinish = (values, actions) => {
        const { accessToken, refreshToken, baseUrl, deviceID, scope, userId, userType, phone } = dataStorage.auth
        const url = 'https://trade-magpie-dev2.magpieinvest.com'
        const link = `${url}?accessToken=${accessToken}&refreshToken=${refreshToken}&baseUrl=${baseUrl}&deviceId=${deviceID}&scope=${scope}&userId=${userId}&userType=${userType}&phone=${phone}`
        actions.setSubmitting(false)
        window.open(url, link)
    }

    function _handleSubmit(values, actions) {
        if (activeStep === submitStep) {
            _submitForm(values, actions);
        } else if (activeStep === lastStep) {
            onFinish(values, actions)
        } else {
            onNext(values, actions)
        }
    }

    const onStepClick = (actions) => (index) => {
        if (index > 1 && index < lastStep) {
            if (index < activeStep) {
                if (listStepCanPress.current[index]) {
                    setActiveStep(index)
                } else _handleBack()
            } else if (index > activeStep) {
                actions.validateForm().then(errors => {
                    if (errors && Object.keys(errors).length) {
                        actions.setTouched(errors)
                    } else {
                        if (listStepCanPress.current[index]) {
                            setActiveStep(index)
                        } else {
                            refNext.current && refNext.current.click()
                        }
                    }
                })
            }
        }
    }

    function _handleBack(setFieldValue) {
        if (activeStep === 0) {
            // handle something
        } else {
            scrollToTop()
            setActiveStep(activeStep - 1);
        }
    }

    const checkBtnDisable = (values) => {
        switch (activeStep) {
            case 1: {
                if (kycResult.current?.PHOTO_MATCH?.decision + kycResult.current?.DOCUMENT?.decision + kycResult.current?.LIVENESS?.decision === 'PASSEDPASSEDPASSED') return false
                return true
            }
            case 5: {
                // Required to select No all
                if (values.identity_disclosure.acting_as_intermediary
                    || values.identity_disclosure.registered_securities_futures
                    || values.identity_disclosure.related_to_public_function
                    || values.identity_disclosure.related_to_magpie
                    || values.identity_disclosure.spouse_margin
                    || values.identity_disclosure.control_magpie_voting) {
                    return true
                }
                return false
            }
            case 7: {
                // Must check all checkbox
                if (!values.risk_disclosure
                    || values.risk_disclosure.length !== 10) {
                    return true
                }
                return false
            }
            default: return false
        }
    }

    const renderButtons = (isSubmitting, values, setFieldValue) => {
        const isHaveBack = activeStep > 2 && activeStep < lastStep
        const textSubmit = activeStep === submitStep ? 'lang_submit_application' : (activeStep === lastStep ? 'lang_initiate_a_fund_transfer_and_deposit' : 'lang_next');
        const isLoading = isSubmitting || (activeStep === 1 && dataStorage.livenessProgress)
        return (
            <div className={classes.buttons}>
                <div />
                <div className={classes.rightButtons}>
                    {
                        isHaveBack
                            ? <div className={classes.wrapper}>
                                <Button onClick={() => _handleBack(setFieldValue)} className={classes.button}>
                                    <Trans>lang_back</Trans>
                                </Button>
                            </div>
                            : <React.Fragment />
                    }
                    <div className={classes.wrapper}>
                        <Button
                            ref={refNext}
                            disabled={isLoading || checkBtnDisable(values)}
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classes.button}
                        >
                            <Trans>{textSubmit}</Trans>
                            {isLoading && (
                                <CircularProgress
                                    size={24}
                                    className={classes.buttonProgress}
                                />
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Formik
            initialValues={initialData.current}
            validationSchema={currentValidationSchema}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={_handleSubmit}
        >
            {({ isSubmitting, setFieldValue, setTouched, errors, values, validateForm, setSubmitting }) => {
                // console.log('YOLO errors: ', errors)
                formValues.current = values
                formActions.current = {
                    setSubmitting
                }
                return (
                    <div className={classes.container}>
                        <Steps
                            steps={ListStep}
                            activeStep={activeStep}
                            listStepCanPress={listStepCanPress.current}
                            // onStepClick={onStepClick({ validateForm, setTouched })} //disable function 
                        />
                        <div className={classes.formContainer}>
                            <FocusError>
                                <Form id={formId} autoComplete='off' noValidate>
                                    {_renderStepContent()}
                                    {renderButtons(isSubmitting, values, setFieldValue, setTouched)}
                                </Form>
                            </FocusError>
                        </div>
                    </div>
                )
            }}
        </Formik>
    );
}
